import React, { useContext, useEffect, useState } from "react";
import { makeStyles, styled } from "@material-ui/core/styles";
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from "@material-ui/lab";
import { Badge, InputAdornment, Paper, Tab, Tabs, TextField, Tooltip, useMediaQuery } from "@material-ui/core";

import Android from '@material-ui/icons/Android';
import AddCommentOutlinedIcon from '@material-ui/icons/AddCommentOutlined';
import BookOutlinedIcon from '@material-ui/icons/BookOutlined';
import ContactsOutlinedIcon from '@material-ui/icons/ContactsOutlined';
import ContactsViewList from "../ContactsViewList";
import EventNoteOutlined from '@material-ui/icons/EventNoteOutlined';
import ModeCommentOutlinedIcon from '@material-ui/icons/ModeCommentOutlined';
import PeopleOutlineOutlinedIcon from '@material-ui/icons/PeopleOutlineOutlined';
import PersonOutlinedIcon from '@material-ui/icons/PersonOutlined';
import QuestionAnswerOutlinedIcon from '@material-ui/icons/QuestionAnswerOutlined';
import RecordVoiceOverOutlinedIcon from '@material-ui/icons/RecordVoiceOverOutlined';
import SearchIcon from "@material-ui/icons/Search";
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import MarkChatsAsReadModal from "../MarkChatsAsReadModal";
import NewInternalChatModal from "../NewInternalChatModal";
import NewInternalGroupModal from "../NewInternalGroupModal";
import NewNoteModal from "../NewNoteModal";
import NewOpenAIChatModal from "../NewOpenAIChatModal";
import NewTicketModal from "../NewTicketModal";
import TabPanel from "../TabPanel";
import TicketsList from "../TicketsList";
import UserSelect from "../UserSelect";
import WhatsappSelect from "../WhatsappSelect";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const StyledSpeedDial = styled(SpeedDial)(({ theme }) => ({
  position: 'absolute',
  '&.MuiSpeedDial-directionUp, &.MuiSpeedDial-directionLeft': {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  '&.MuiSpeedDial-directionDown, &.MuiSpeedDial-directionRight': {
    top: theme.spacing(2),
    left: theme.spacing(2),
  },
}));

const StyledBotButton = styled(SpeedDial)(({ theme }) => ({
  position: 'absolute',
  '&.MuiSpeedDial-directionUp, &.MuiSpeedDial-directionLeft': {
    bottom: theme.spacing(2),
    left: "15px",
  },
}));

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    borderRadius:"20px",
    position: "relative",
    display: "flex",
    minHeight: "350px",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
  },

  ticketManagerButtonsContainer: { height: "15%", },

  tab: { borderRadius:"20px", minWidth: 120, width: 120, },
  
  badge: { borderRadius:"20px", right: "-10px", },
  
  speedDialIconButton: { borderRadius:"20px", color: theme.palette.text.primary, },

  inputField: {
    alignSelf: "center",
    width: "98%",
    height: "50px",

    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.text.secondary,
        borderRadius: "20px",
        height: "50px",
      },
    },
  },

  selectFilters:{
    padding : '0px 1em 1em 1em',

    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: "5px",

    [theme.breakpoints.down("sm")]: { flexDirection: "column", },
  },

  selectContainer: {
    width: "50%",
    [theme.breakpoints.down("sm")]: { width: "100%", }
  },

  selectContainerFullWidth: {
    width: "100%",
    [theme.breakpoints.down("sm")]: { width: "100%", }
  },
}));

const TicketsManager = ({ tabOpen, setTabOpen, resetAudioPlayer }) => {
  //  ***************
  //  ** Variables **
  //  ***************

  // ***---- General Variables ----***
  const classes = useStyles();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);

  // ***---- Actions ----***
  const [open, setOpen] = React.useState(false);
  const [newInternalChatModalOpen, setNewInternalChatModalOpen] = useState(false);
  const [newInternalGroupModalOpen, setNewInternalGroupModalOpen] = useState(false);
  const [newNoteModalOpen, setNewNoteModalOpen] = useState(false);
  const [newOpenAiModalOpen, setNewOpenAiModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [markChatsAsReadModalOpen, setMarkChatsAsReadModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(localStorage.getItem("showAllTickets") === "1" ? true : false);

  // ***---- Counts ----***
  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [groupCount, setGroupCount] = useState(0);

  // ***---- Filters: Users ----***
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  // ***---- Filters: Connections ----***
  const [contactsViewSelectedConnectionIds, setContactsViewSelectedConnectionIds] = useState([]);
  const [openSelectedConnectionIds, setOpenSelectedConnectionIds] = useState([]);
  const [pendingSelectedConnectionIds, setPendingSelectedConnectionIds] = useState([]);
  const [closedSelectedConnectionIds, setClosedSelectedConnectionIds] = useState([]);

  // ***---- Filters: Text ----***
  const [closedFilterValue, setClosedFilterValue] = useState("");
  const [groupFilterValue, setGroupFilterValue] = useState("");
  const [contactsViewFilterValue, setContactsViewFilterValue] = useState("");
  const [openFilterValue, setOpenFilterValue] = useState("");
  const [pendingFilterValue, setPendingFilterValue] = useState("");

  // ***---- Settings ----***
  const [allowGroups, setAllowGroups] = useState("disabled");
  const [shouldEnablePendingTickets, setShouldEnablePendingTickets] = useState("disabled");
  const [shouldEnableContactsView, setShouldEnableContactsView] = useState(false);

  // ***---- URL Query ----***
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const tabRequest = (urlParams.get('tab') ? urlParams.get('tab') : "open");
  const [tab, ] = useState("open");

  // ***---- Settings ----***
  const { getSetting, allUsers, allWhatsapps, isLoading } = useGlobalRegisters();



  //  *************
  //  ** Actions **
  //  *************
  const actions = [
    { 
      icon: <AddCommentOutlinedIcon className={classes.speedDialIconButton}/>, 
      name: "NewChat",
      caption: i18n.t("ticketsManager.buttons.newTicket"), 
      onClick: () => setNewTicketModalOpen(true)
    },
    { 
      icon: <EventNoteOutlined className={classes.speedDialIconButton}/>, 
      name: "NewNote",
      caption: i18n.t("ticketsManager.buttons.newNote"), 
      onClick: () => setNewNoteModalOpen(true)
    },
    {
      icon: <ModeCommentOutlinedIcon className={classes.speedDialIconButton}/>,
      name: "NewInternalChat",
      caption: i18n.t("ticketsManager.buttons.newInternalChat"),
      onClick: () => setNewInternalChatModalOpen(true)
    },
    {
      icon: <BookOutlinedIcon className={classes.speedDialIconButton} />,
      name: 'MarkChatsAsRead',
      caption: i18n.t("ticketsManager.buttons.markChatsAsRead"),
      onClick: () => setMarkChatsAsReadModalOpen(true)
    },
  ];

  if (allowGroups === "enabled") {
    actions.splice(3, 0, {
      icon: <QuestionAnswerOutlinedIcon className={classes.speedDialIconButton} />,
      name: "NewInternalGroup",
      caption: i18n.t("ticketsManager.buttons.newInternalGroup"),
      onClick: () => setNewInternalGroupModalOpen(true)
    });
  }

  if (user.profile === "admin") {
    actions.push({ 
      icon: (showAllTickets ? <VisibilityOffOutlinedIcon className={classes.speedDialIconButton}/> : <VisibilityOutlinedIcon className={classes.speedDialIconButton}/>), 
      name: 'ShowAll', 
      caption: (showAllTickets ? i18n.t("ticketsManager.buttons.showAllOff") : i18n.t("ticketsManager.buttons.showAll")),
      onClick: () => { 
        setShowAllTickets((prevState) => !prevState);
        localStorage.setItem("showAllTickets", localStorage.getItem("showAllTickets") === "1" ? "0" : "1");
      }
    });
  }



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (isLoading) return;

    setAllowGroups(getSetting("allowGroups"));

    const adminsPendingTicketsControl = getSetting("adminsPendingTicketsControl");
    setShouldEnablePendingTickets(adminsPendingTicketsControl === "disabled" || (adminsPendingTicketsControl === "enabled" && user.profile === "admin"));

    const allowContactsView = getSetting("allowContactsView");
    const allowContactsViewToTypeOfUsers = getSetting("allowContactsViewToTypeOfUsers");
    setShouldEnableContactsView(allowContactsView === "enabled" && (allowContactsViewToTypeOfUsers === "all" || (allowContactsViewToTypeOfUsers === "admins" && user.profile === "admin")));

    if (adminsPendingTicketsControl === "enabled" && user.profile === "user" && tabOpen === "pending") {
      setTabOpen("open");
      localStorage.setItem("tabOpenTickets", "open");
    }
  }, [getSetting, isLoading, setTabOpen, tabOpen, user]);

  useEffect(() => {
    if (user.profile.toUpperCase() === "USER") {
      setShowAllTickets(false);
      localStorage.setItem("showAllTickets", "0");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tabRequest === "closed") {
      setContactsViewFilterValue("");
      setGroupFilterValue("");
      setOpenFilterValue("");
      setPendingFilterValue("");
    }

    if (tabRequest === "open") {
      setClosedFilterValue("");
    }

    if (tabRequest === "open" && localStorage.getItem("tabOpenTickets") === "closed") {
      localStorage.setItem("tabOpenTickets", "open");
    }
  }, [tabRequest]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleChangeTabOpen = (event, newValue) => {
    setContactsViewFilterValue("");
    setClosedFilterValue("");
    setGroupFilterValue("");
    setOpenFilterValue("");
    setPendingFilterValue("");
    setTabOpen(newValue);
    localStorage.setItem("tabOpenTickets", newValue);
  };

  const handleOpenClose = () => {
    setOpen(previousValue => !previousValue);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) return { width: 0, height: 0 };
  };

  const handleTextViewFilter = (event, tabRequest, tabOpen) => {
    if (tabRequest === "closed") setClosedFilterValue(event.target.value);
    else if (tabOpen === "group") setGroupFilterValue(event.target.value);
    else if (tabOpen === "contactsView") setContactsViewFilterValue(event.target.value);
    else if (tabOpen === "open") setOpenFilterValue(event.target.value);
    else if (tabOpen === "pending") setPendingFilterValue(event.target.value);
  };


  
  //  ************
  //  ** Return **
  //  ************
  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      {/* 
    
        ***---- Modals ----***
    
      */}
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
        setTabOpen={setTabOpen}
      />
    
      <NewNoteModal
        modalOpen={newNoteModalOpen}
        onClose={() => setNewNoteModalOpen(false)}
        setTabOpen={setTabOpen}
      />

      <NewInternalChatModal
        modalOpen={newInternalChatModalOpen}
        onClose={() => setNewInternalChatModalOpen(false)}
        setTabOpen={setTabOpen}
      />

      <MarkChatsAsReadModal
        modalOpen={markChatsAsReadModalOpen}
        onClose={() => setMarkChatsAsReadModalOpen(false)}
      />
      
      <NewInternalGroupModal
        modalOpen={newInternalGroupModalOpen}
        onClose={() => setNewInternalGroupModalOpen(false)}
        setTabOpen={setTabOpen}
      />

      <NewOpenAIChatModal
        modalOpen={newOpenAiModalOpen}
        onClose={() => setNewOpenAiModalOpen(false)}
        setTabOpen={setTabOpen}
      />

      {/* 
      
        ***---- Tabs ----***

      */}
      {tabRequest === "open" && (
        <TabPanel value={tab} name={"open"} className={classes.ticketsWrapper}>
          {/* 
            **********
            ** Tabs **
            **********
          */}
          <Tabs
            value={tabOpen}
            indicatorColor="primary"
            textColor="primary"
            scrollButtons="auto"
            variant={!isSmallScreen && allowGroups === "enabled" && shouldEnableContactsView ? "scrollable" : "fullWidth"}
            centered={!isSmallScreen}
            onChange={handleChangeTabOpen}
          >
            {allowGroups === "enabled" && (
              <Tab
                label={
                  <Badge className={classes.badge} badgeContent={groupCount} color="primary">
                    {isSmallScreen ? <PeopleOutlineOutlinedIcon /> : i18n.t("ticketsList.groupHeader")}
                  </Badge>
                }
                value={"group"}
              />
            )}

            {shouldEnableContactsView && (
              <Tab
                label={
                  <Badge className={classes.badge} badgeContent={0} color="primary">
                    {isSmallScreen ? <ContactsOutlinedIcon /> : i18n.t("ticketsList.contactsViewHeader")}
                  </Badge>
                }
                value={"contactsView"}
              />
            )}

            <Tab
              label={
                <Badge className={classes.badge} badgeContent={openCount} color="primary">
                  {isSmallScreen ? <PersonOutlinedIcon/> : i18n.t("ticketsList.assignedHeader")}
                </Badge>
              }
              value={"open"}
            />

            {shouldEnablePendingTickets && (
              <Tab
                label={
                  <Badge className={classes.badge} badgeContent={pendingCount} color="primary">
                    {isSmallScreen ? <RecordVoiceOverOutlinedIcon /> : i18n.t("ticketsList.pendingHeader")}
                  </Badge>
                }
                value={"pending"}
              />
            )}
          </Tabs>

          

          {/* 
            ********************************
            ** Tabs - Text - ContactsView **
            ********************************
          */}
          {tabOpen === "contactsView" && shouldEnableContactsView && (
            <TextField
              value={contactsViewFilterValue}
              onChange={(event) => handleTextViewFilter(event, tabRequest, tabOpen)}
              variant="outlined"
              margin="normal"
              type="text"
              id="messageKeywords"
              name="messageKeywords"
              className={classes.inputField}
              InputProps={{startAdornment: (<InputAdornment position="center"><SearchIcon /></InputAdornment>)}}
            />
          )}



          {/* 
            ****************************
            ** Filters - Text - Group **
            ****************************
          */}
          {tabOpen === "group" && (
            <TextField
              value={groupFilterValue}
              onChange={(event) => handleTextViewFilter(event, tabRequest, tabOpen)}
              variant="outlined"
              margin="normal"
              type="text"
              id="messageKeywords"
              name="messageKeywords"
              className={classes.inputField}
              InputProps={{startAdornment: (<InputAdornment position="center"><SearchIcon /></InputAdornment>)}}
            />
          )}



          {/* 
            *********************************
            ** Filters - Text - Working On **
            *********************************
          */}
          {tabOpen === "open" && (
            <TextField
              value={openFilterValue}
              onChange={(event) => handleTextViewFilter(event, tabRequest, tabOpen)}
              variant="outlined"
              margin="normal"
              type="text"
              id="messageKeywords"
              name="messageKeywords"
              className={classes.inputField}
              InputProps={{startAdornment: (<InputAdornment position="center"><SearchIcon /></InputAdornment>)}}
            />
          )}



          {/* 
            ******************************
            ** Filters - Text - Pending **
            ******************************
          */}
          {tabOpen === "pending" && shouldEnablePendingTickets && (
            <TextField
              value={pendingFilterValue}
              onChange={(event) => handleTextViewFilter(event, tabRequest, tabOpen)}
              variant="outlined"
              margin="normal"
              type="text"
              id="messageKeywords"
              name="messageKeywords"
              className={classes.inputField}
              InputProps={{startAdornment: (<InputAdornment position="center"><SearchIcon /></InputAdornment>)}}
            />
          )}



          {/*
            ******************************************
            ** Filters - Connections - ContactsView **
            ******************************************
          */}
          {tabOpen === "contactsView" && (
            <div className={classes.selectContainerFullWidth}>
              <WhatsappSelect
                selectedWhatsappIds={contactsViewSelectedConnectionIds}
                onChange={values => setContactsViewSelectedConnectionIds(values)}
                whatsappsOptions={allWhatsapps}
                styleSelectInputField={true}
              />
            </div>
          )}

          

          {/* 
            *******************************************
            ** Filters - User and Connections - Open **
            *******************************************
          */}
          {user.profile === "admin" && tabOpen === "open" && (
            <div className={classes.selectFilters}>
              <div className={classes.selectContainer}>
                <UserSelect
                  selectedUserIds={selectedUserIds}
                  onChange={values => setSelectedUserIds(values)}
                  usersOptions={allUsers}
                  styleSelectInputField={true}
                />
              </div>

              <div className={classes.selectContainer}>
                <WhatsappSelect
                  selectedWhatsappIds={openSelectedConnectionIds}
                  onChange={values => setOpenSelectedConnectionIds(values)}
                  whatsappsOptions={allWhatsapps}
                  styleSelectInputField={true}
                />
              </div>
            </div>
          )}

          {/* 
            *************************************
            ** Filters - Connections - Pending **
            *************************************
          */}
          {user.profile === "admin" && tabOpen === "pending" && (
            <div className={classes.selectFilters}>
              <div className={classes.selectContainerFullWidth}>
                <WhatsappSelect
                  selectedWhatsappIds={pendingSelectedConnectionIds}
                  onChange={values => setPendingSelectedConnectionIds(values)}
                  whatsappsOptions={allWhatsapps}
                  styleSelectInputField={true}
                />
              </div>
            </div>
          )}

          {/* 
            ******************
            ** Ticket Lists **
            ******************
          */}
          <Paper className={classes.ticketsWrapper}>
            <TicketsList
              status="group"
              isGroup="true"
              direction="up"
              updateCount={(val) => setGroupCount(val)}
              style={applyPanelStyle("group")}
              filter={groupFilterValue}
              setTabOpen={setTabOpen}
              resetAudioPlayer={resetAudioPlayer}
            />

            <TicketsList
              status="open"
              isGroup="false"
              showAll={showAllTickets}
              updateCount={(val) => setOpenCount(val)}
              style={applyPanelStyle("open")}
              filter={openFilterValue}
              selectedUserIds={selectedUserIds}
              selectedConnectionIds={openSelectedConnectionIds}
              setTabOpen={setTabOpen}
              resetAudioPlayer={resetAudioPlayer}
            />
            
            {shouldEnablePendingTickets && (
              <TicketsList
                status="pending"
                isGroup="false"
                updateCount={(val) => setPendingCount(val)}
                style={applyPanelStyle("pending")}
                filter={pendingFilterValue}
                selectedConnectionIds={pendingSelectedConnectionIds}
                setTabOpen={setTabOpen}
                resetAudioPlayer={resetAudioPlayer}
              />
            )}
            
            {shouldEnableContactsView && (
              <ContactsViewList
                filter={contactsViewFilterValue}
                selectedConnectionIds={contactsViewSelectedConnectionIds}
                resetAudioPlayer={resetAudioPlayer}
                style={applyPanelStyle("contactsView")}
              />
            )}

            <div className={classes.ticketManagerButtonsContainer}>
              {/* 
                ****************
                ** Speed Dial **
                ****************
              */}
              <StyledSpeedDial
                ariaLabel="SpeedDial"
                direction="up"
                icon={<SpeedDialIcon />}
                onClick={handleOpenClose}
                open={open}
              >
                {actions.map((action) => (
                  <SpeedDialAction
                    key={action.name}
                    icon={action.icon}
                    tooltipTitle={action.caption}
                    onClick={action.onClick}
                  />
                ))}
              </StyledSpeedDial>

            

              {/* 
                ****************
                ** Bot Button **
                ****************
              */}
              <Tooltip title={i18n.t("openAi.expander")} placement="top-start" arrow>
                <StyledBotButton
                  ariaLabel="SpeedDial"
                  direction="up"
                  icon={<Android />}
                  onClick= {() => setNewOpenAiModalOpen(true)}
                />
              </Tooltip>
            </div>
          </Paper>
        </TabPanel>
      )}
      
      {tabRequest === "closed" && (
        <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
          {/* 
            *********************
            ** Filter - Closed **
            *********************
          */}
          <TextField
            value={closedFilterValue}
            onChange={(event) => handleTextViewFilter(event, tabRequest, tabOpen)}
            variant="outlined"
            margin="normal"
            type="text"
            id="messageKeywords"
            name="messageKeywords"
            className={classes.inputField}
            InputProps={{ startAdornment: (<InputAdornment postion="center"><SearchIcon /></InputAdornment>) }}
          />



          {/*
            *********************************************
            ** Filters - User and Connections - Closed **
            *********************************************
          */}
          {user.profile === "admin" && (
            <div className={classes.selectFilters}>
              <div className={classes.selectContainer}>
                <UserSelect
                  selectedUserIds={selectedUserIds}
                  onChange={values => setSelectedUserIds(values)}
                  usersOptions={allUsers}
                  styleSelectInputField={true}
                />
              </div>

              <div className={classes.selectContainer}>
                <WhatsappSelect
                  selectedWhatsappIds={closedSelectedConnectionIds}
                  onChange={values => setClosedSelectedConnectionIds(values)}
                  whatsappsOptions={allWhatsapps}
                  styleSelectInputField={true}
                />
              </div>
            </div>
          )}



          {/* 
            ******************
            ** Tickets List **
            ******************
          */}
          <TicketsList
            status="closed"
            isGroup="false"
            filter={closedFilterValue}
            showAll={showAllTickets}
            selectedUserIds={selectedUserIds}
            selectedConnectionIds={closedSelectedConnectionIds}
            setTabOpen={setTabOpen}
            resetAudioPlayer={resetAudioPlayer}
          />

          <div className={classes.ticketManagerButtonsContainer}>
            {/* 
              ****************
              ** Speed Dial **
              ****************
            */}
            <StyledSpeedDial
              ariaLabel="SpeedDial"
              direction="up"
              icon={<SpeedDialIcon />}
              onClick={handleOpenClose}
              open={open}
            >
              {actions.map((action) => (
                <SpeedDialAction
                  key={action.name}
                  icon={action.icon}
                  tooltipTitle={action.caption}
                  onClick={action.onClick}
                />
              ))}
            </StyledSpeedDial>

            {/* 
              ****************
              ** Bot Button **
              ****************
            */}
            <Tooltip title={i18n.t("openAi.expander")} placement="top-start" arrow>
              <StyledBotButton
                ariaLabel="SpeedDial"
                direction="up"
                icon={<Android />}
                onClick= {() => setNewOpenAiModalOpen(true)}
              />
            </Tooltip>
          </div>
        </TabPanel>
      )}
    </Paper>
  );
};

export default TicketsManager;
